import type { InferenceResultType } from './inference-results-data';

/** ARIs are passed in Graph Store parameters, never interpolated into Cypher. */
export const inferenceDetailQuery = (type: Exclude<InferenceResultType, 'all'>): string => {
	switch (type) {
		case 'project':
			return `MATCH (ip:InferredProject {ari: $node})
				OPTIONAL MATCH (ip)-[:inferred_project_links_entity]->(linkedEntity)
				WITH ip, collect(DISTINCT linkedEntity) AS linkedEntities
				OPTIONAL MATCH (inferredTeam:InferredTeam)-[:inferred_team_collaborates_on_inferred_project]->(ip)
				OPTIONAL MATCH (inferredTeam)-[:inferred_team_links_entity]->(inferredTeamBacking)
				WITH ip, linkedEntities, inferredTeam, inferredTeamBacking
				OPTIONAL MATCH (owner:IdentityUser)-[:atlassian_user_owns_inferred_project]->(ip)
				WITH ip, linkedEntities, inferredTeam, inferredTeamBacking, collect(DISTINCT owner) AS owners
				OPTIONAL MATCH (collaborator:IdentityUser)-[:atlassian_user_collaborates_on_inferred_project]->(ip)
				RETURN ip, linkedEntities, inferredTeam, inferredTeamBacking, owners, collect(DISTINCT collaborator) AS collaborators
				LIMIT 1000`;
		case 'decision':
			// Keep the temporary Townsquare/Jira scope in sync with the expanded decision list.
			// Remove it once InferredProject impact permission checks are fixed.
			return `MATCH (ip:InferredDecision {ari: $node})
				OPTIONAL MATCH (ip)-[:inferred_decision_links_entity]->(linkedEntity)
				WITH ip, collect(DISTINCT linkedEntity) AS linkedEntities
				OPTIONAL MATCH (ip)-[:inferred_decision_impacts_entity]->(impactedProject:TownsquareProject)
				WITH ip, linkedEntities, collect(DISTINCT impactedProject) AS impactedProjects
				OPTIONAL MATCH (ip)-[:inferred_decision_impacts_entity]->(impactedGoal:TownsquareGoal)
				WITH ip, linkedEntities, impactedProjects, collect(DISTINCT impactedGoal) AS impactedGoals
				OPTIONAL MATCH (ip)-[:inferred_decision_impacts_entity]->(impactedIssue:JiraIssue)
				WITH ip, linkedEntities, impactedProjects, impactedGoals, collect(DISTINCT impactedIssue) AS impactedIssues
				RETURN ip, linkedEntities, impactedProjects + impactedGoals + impactedIssues AS impactedEntities
				LIMIT 1000`;
		case 'team':
			return `MATCH (it:InferredTeam {ari: $node})
				OPTIONAL MATCH (it)-[:inferred_team_links_entity]->(jp)
				OPTIONAL MATCH (it)-[:inferred_team_collaborates_on_inferred_project]->(ip:InferredProject)
				RETURN DISTINCT it, jp, ip LIMIT 1000`;
		case 'risk':
			return `MATCH (ir:InferredRisk {ari: $node})
				OPTIONAL MATCH (ir)-[:inferred_risk_links_entity]->(linkedEntity)
				WITH ir, collect(DISTINCT linkedEntity) AS linkedEntities
				OPTIONAL MATCH (ir)-[:inferred_risk_impacts_entity]->(impactedEntity)
				RETURN ir, linkedEntities, collect(DISTINCT impactedEntity) AS impactedEntities
				LIMIT 1000`;
		case 'highlight':
			return `MATCH (ih:InferredHighlight {ari: $node})
				OPTIONAL MATCH (ih)-[:inferred_highlight_links_entity]->(linkedEntity)
				WITH ih, collect(DISTINCT linkedEntity) AS linkedEntities
				OPTIONAL MATCH (ih)-[:inferred_highlight_impacts_entity]->(impactedEntity)
				RETURN ih, linkedEntities, collect(DISTINCT impactedEntity) AS impactedEntities
				LIMIT 1000`;
		case 'actionItem':
			return `MATCH (ia:InferredActionItem {ari: $node})
				OPTIONAL MATCH (ia)-[:inferred_action_item_links_entity]->(linkedEntity)
				WITH ia, collect(DISTINCT linkedEntity) AS linkedEntities
				OPTIONAL MATCH (ia)-[:inferred_action_item_impacts_entity]->(impactedEntity)
				RETURN ia AS ia, linkedEntities AS linkedEntities, collect(DISTINCT impactedEntity) AS impactedEntities
				LIMIT 1000`;
		case 'artifact':
			return `MATCH (project)-[relationship:atlassian_project_has_related_entity|inferred_project_has_related_entity]->(artifact {ari: $node})
				WHERE $source IS NULL OR project.ari = $source
				RETURN DISTINCT project, artifact, relationship.relevance AS relevance,
				relationship.provenance.appId AS appId, relationship.provenance.appVersion AS appVersion
				LIMIT 1000`;
	}
};
